import { z } from 'zod';
import { UserRole } from '../enums/roles';

/**
 * Impersonation DTOs (Issue #321)
 *
 * Schemas for user impersonation feature enabling super admins and authorized
 * business users to impersonate other users/consumers for customer success operations.
 *
 * Key security requirements:
 * - Super admins can impersonate any non-super-admin user or consumer
 * - Business users with CONSUMERS_IMPERSONATE permission can only impersonate consumers in their business
 * - All actions during impersonation are attributed to the actor (impersonator)
 * - Sessions are time-limited (max 30 minutes)
 *
 * NOTE: ImpersonationContext and ImpersonationContextSchema are defined in auth.dto.ts
 * and exported from there to avoid circular dependencies and maintain JWT-related
 * schemas in one place.
 */

// ============================================================================
// Target Types
// ============================================================================

/**
 * Type of entity being impersonated
 */
export const ImpersonationTargetTypeSchema = z.enum(['user', 'consumer']);
export type ImpersonationTargetType = z.infer<typeof ImpersonationTargetTypeSchema>;

/**
 * Enum object for ImpersonationTargetType (for use in comparisons)
 * Usage: ImpersonationTargetType.USER === 'user'
 */
export const ImpersonationTargetType = {
  USER: 'user' as const,
  CONSUMER: 'consumer' as const,
} as const;

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Start impersonation request
 */
export const StartImpersonationRequestSchema = z.object({
  /** ID of the user or consumer to impersonate */
  targetId: z.string().uuid({ message: 'Invalid target ID format' }),

  /** Type of target: 'user' or 'consumer' */
  targetType: ImpersonationTargetTypeSchema,

  /** Mandatory reason for audit compliance */
  reason: z
    .string()
    .min(10, 'Reason must be at least 10 characters for compliance')
    .max(500, 'Reason must be under 500 characters'),

  /** Session duration in minutes (5-30, default 30) */
  durationMinutes: z
    .number()
    .int()
    .min(5, 'Minimum duration is 5 minutes')
    .max(30, 'Maximum duration is 30 minutes')
    .default(30),

  /**
   * Target's business ID (required for super admin cross-business impersonation)
   * Super admins must provide this when impersonating users/consumers in other businesses
   */
  targetBusinessId: z.string().uuid().optional(),
});

export type StartImpersonationRequest = z.infer<typeof StartImpersonationRequestSchema>;

/**
 * End impersonation request (uses session from JWT context)
 */
export const EndImpersonationRequestSchema = z.object({
  /** Optional explicit session ID (defaults to current session from JWT) */
  sessionId: z.string().uuid().optional(),
});

export type EndImpersonationRequest = z.infer<typeof EndImpersonationRequestSchema>;

/**
 * Revoke impersonation session (admin action to force-end another session)
 */
export const RevokeImpersonationRequestSchema = z.object({
  /** Session ID to revoke */
  sessionId: z.string().uuid(),

  /** Reason for revocation */
  reason: z.string().min(10, 'Revocation reason must be at least 10 characters'),
});

export type RevokeImpersonationRequest = z.infer<typeof RevokeImpersonationRequestSchema>;

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Target user/consumer info in response
 */
export const ImpersonationTargetInfoSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(), // UserRole for users, 'consumer' for consumers
  businessId: z.string().uuid(),
});

export type ImpersonationTargetInfo = z.infer<typeof ImpersonationTargetInfoSchema>;

/**
 * Start impersonation response
 */
export const StartImpersonationResponseSchema = z.object({
  success: z.literal(true),

  /** JWT access token with impersonation context */
  accessToken: z.string(),

  /** Token expiration in seconds */
  expiresIn: z.number().int(),

  /** Session ID for tracking */
  sessionId: z.string().uuid(),

  /** Target user/consumer info */
  target: ImpersonationTargetInfoSchema,

  /** URL to redirect to (Business Portal or Consumer Portal) */
  portalUrl: z.string().url(),
});

export type StartImpersonationResponse = z.infer<typeof StartImpersonationResponseSchema>;

/**
 * End impersonation response
 */
export const EndImpersonationResponseSchema = z.object({
  success: z.literal(true),

  /** Session ID that was ended */
  sessionId: z.string().uuid(),

  /** Number of actions performed during session */
  actionCount: z.number().int(),

  /** Session duration in seconds */
  durationSeconds: z.number().int(),

  /** Human-readable message */
  message: z.string(),
});

export type EndImpersonationResponse = z.infer<typeof EndImpersonationResponseSchema>;

/**
 * Impersonation status response
 */
export const ImpersonationStatusResponseSchema = z.discriminatedUnion('active', [
  // Not impersonating
  z.object({
    active: z.literal(false),
  }),
  // Currently impersonating
  z.object({
    active: z.literal(true),
    target: ImpersonationTargetInfoSchema,
    actor: z.object({
      id: z.string().uuid(),
      email: z.string().email(),
      role: z.nativeEnum(UserRole),
    }),
    reason: z.string(),
    sessionId: z.string().uuid(),
    startedAt: z.number().int(),
    remainingSeconds: z.number().int(),
  }),
]);

export type ImpersonationStatusResponse = z.infer<typeof ImpersonationStatusResponseSchema>;

/**
 * Active impersonation session (for admin list view)
 */
export const ActiveImpersonationSessionSchema = z.object({
  sessionId: z.string().uuid(),
  actor: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
  }),
  target: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    type: ImpersonationTargetTypeSchema,
  }),
  reason: z.string(),
  startedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  actionCount: z.number().int(),
});

export type ActiveImpersonationSession = z.infer<typeof ActiveImpersonationSessionSchema>;

/**
 * List active impersonation sessions response
 */
export const ListActiveImpersonationsResponseSchema = z.object({
  sessions: z.array(ActiveImpersonationSessionSchema),
  total: z.number().int(),
});

export type ListActiveImpersonationsResponse = z.infer<typeof ListActiveImpersonationsResponseSchema>;
